import React, { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin } from 'lucide-react';
import { api } from '../services/api';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../stores/authStore';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface FaseAtiva {
  id: number;
  nome_fase: string;
  status_fase: string;
  endereco_destino: string;
  motorista_nome?: string;
  motorista_email?: string;
  lat?: number | null;
  lng?: number | null;
  ultima_atualizacao?: string;
  status_rastreamento?: string;
}

interface OSAtiva {
  id: number;
  codigo_acesso: string;
  nome_falecido: string;
  status_geral: string;
  fases: FaseAtiva[];
}

interface ActiveMarker {
  faseId: number;
  lat: number;
  lng: number;
}

interface TrackingMapProps {
  className?: string;
  showHeader?: boolean;
}

const DEFAULT_CENTER: L.LatLngExpression = [-23.5505, -46.6333];
const DEFAULT_ZOOM = 10;
const MIN_ZOOM = 10;
const MAX_FIT_ZOOM = 15;
const VIEWPORT_MARGIN_RATIO = 0.1;
const RECALIBRATE_INTERVAL_MS = 3 * 60 * 1000;
const FIT_BOUNDS_PADDING: L.FitBoundsOptions = {
  padding: [40, 40],
  maxZoom: MAX_FIT_ZOOM,
  animate: true,
  duration: 0.8
};

const extractActiveMarkers = (osList: OSAtiva[]): ActiveMarker[] => {
  const markers: ActiveMarker[] = [];

  osList.forEach((os) => {
    const faseAtiva = os.fases.find((fase) => fase.status_fase === 'Em Rota');
    if (faseAtiva && faseAtiva.lat != null && faseAtiva.lng != null) {
      markers.push({
        faseId: faseAtiva.id,
        lat: Number(faseAtiva.lat),
        lng: Number(faseAtiva.lng)
      });
    }
  });

  return markers;
};

const getPaddedBounds = (map: L.Map): L.LatLngBounds => {
  const bounds = map.getBounds();
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();
  const latSpan = northEast.lat - southWest.lat;
  const lngSpan = northEast.lng - southWest.lng;
  const marginLat = latSpan * VIEWPORT_MARGIN_RATIO;
  const marginLng = lngSpan * VIEWPORT_MARGIN_RATIO;

  return L.latLngBounds(
    [southWest.lat + marginLat, southWest.lng + marginLng],
    [northEast.lat - marginLat, northEast.lng - marginLng]
  );
};

const allPointsFitInView = (map: L.Map, points: ActiveMarker[]): boolean => {
  if (points.length === 0) return true;
  const paddedBounds = getPaddedBounds(map);
  return points.every((point) => paddedBounds.contains([point.lat, point.lng]));
};

const fitAllMarkers = (map: L.Map, points: ActiveMarker[]) => {
  if (points.length === 0) {
    map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: true, duration: 0.8 });
    return;
  }

  if (points.length === 1) {
    map.setView([points[0].lat, points[0].lng], Math.min(MAX_FIT_ZOOM, 14), {
      animate: true,
      duration: 0.8
    });
    return;
  }

  const bounds = L.latLngBounds([]);
  points.forEach((point) => bounds.extend([point.lat, point.lng]));
  map.fitBounds(bounds, FIT_BOUNDS_PADDING);
};

const ensureMarkersVisible = (map: L.Map, points: ActiveMarker[]) => {
  if (points.length === 0) {
    map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: true, duration: 0.8 });
    return;
  }

  const paddedBounds = getPaddedBounds(map);
  const outsidePoints = points.filter((point) => !paddedBounds.contains([point.lat, point.lng]));

  if (outsidePoints.length === 0) return;

  if (!allPointsFitInView(map, points) || outsidePoints.length > 1) {
    fitAllMarkers(map, points);
    return;
  }

  map.panTo([outsidePoints[0].lat, outsidePoints[0].lng], {
    animate: true,
    duration: 0.5
  });
};

const markerSetChanged = (previousIds: Set<number>, currentMarkers: ActiveMarker[]) => {
  const currentIds = new Set(currentMarkers.map((marker) => marker.faseId));
  if (currentIds.size !== previousIds.size) return true;
  return [...currentIds].some((id) => !previousIds.has(id));
};

const TrackingMap: React.FC<TrackingMapProps> = ({ className = 'h-96', showHeader = true }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [osAtivas, setOSAtivas] = useState<OSAtiva[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const hasLoadedRef = useRef(false);
  const osAtivasRef = useRef<OSAtiva[]>([]);
  const activeFaseIdsRef = useRef<Set<number>>(new Set());
  const socketRef = useRef<Socket | null>(null);
  const markersRef = useRef<Map<number, L.Marker>>(new Map());
  const routesRef = useRef<Map<number, L.Polyline>>(new Map());
  const { token, isAuthenticated } = useAuthStore();

  osAtivasRef.current = osAtivas;

  const calculateRoute = async (origin: [number, number], destination: string) => {
    try {
      const geocodingResponse = await api.get(`/geocoding/geocode?address=${encodeURIComponent(destination)}`);

      if (geocodingResponse.data.success && geocodingResponse.data.data) {
        const destCoords = geocodingResponse.data.data;
        const routingResponse = await api.get(
          `/routing/route?origin=${origin[0]},${origin[1]}&destination=${destCoords.lat},${destCoords.lng}`
        );

        if (routingResponse.data.success && routingResponse.data.data) {
          return routingResponse.data.data.route;
        }
      }
    } catch (error) {
      console.error('Erro ao calcular rota:', error);
    }
    return null;
  };

  const createDriverIcon = () => {
    return L.divIcon({
      className: 'driver-marker',
      html: `
        <div style="
          background: linear-gradient(145deg, #1E293B 0%, #0F172A 100%);
          width: 48px;
          height: 48px;
          border-radius: 12px;
          border: 2px solid rgba(255,255,255,0.9);
          box-shadow:
            0 6px 20px rgba(0,0,0,0.15),
            0 0 0 1px rgba(255,255,255,0.2),
            inset 0 1px 0 rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          position: relative;
        ">
          🚗
          <div style="
            position: absolute;
            bottom: -6px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 6px solid #1E293B;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
          "></div>
        </div>
      `,
      iconSize: [48, 48],
      iconAnchor: [24, 54]
    });
  };

  const buildPopupContent = (os: OSAtiva, faseAtiva: FaseAtiva, lat: number, lng: number) => {
    const ultimaAtualizacao = faseAtiva.ultima_atualizacao
      ? new Date(faseAtiva.ultima_atualizacao).toLocaleString('pt-BR')
      : 'Não disponível';

    return `
      <div class="p-4 min-w-64">
        <div class="flex items-center space-x-3 mb-3">
          <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">🚗</div>
          <div>
            <h3 class="font-semibold text-gray-900">${faseAtiva.motorista_nome || 'Motorista'}</h3>
            <p class="text-sm text-gray-600">Motorista</p>
          </div>
        </div>
        <div class="space-y-2">
          <div class="flex justify-between"><span class="text-sm font-medium text-gray-700">OS:</span><span class="text-sm text-gray-900">${os.codigo_acesso}</span></div>
          <div class="flex justify-between"><span class="text-sm font-medium text-gray-700">Falecido:</span><span class="text-sm text-gray-900">${os.nome_falecido}</span></div>
          <div class="flex justify-between"><span class="text-sm font-medium text-gray-700">Fase:</span><span class="text-sm text-blue-600 font-medium">${faseAtiva.nome_fase}</span></div>
          <div class="flex justify-between"><span class="text-sm font-medium text-gray-700">Status:</span><span class="text-sm text-blue-600 font-medium">${faseAtiva.status_fase}</span></div>
        </div>
        <hr class="my-3 border-gray-200">
        <div class="space-y-2">
          <div class="flex justify-between"><span class="text-sm font-medium text-gray-700">📍 Posição:</span><span class="text-sm text-gray-900 font-mono">${lat.toFixed(6)}, ${lng.toFixed(6)}</span></div>
          <div class="flex justify-between"><span class="text-sm font-medium text-gray-700">🕒 Última Atualização:</span><span class="text-sm text-gray-900">${ultimaAtualizacao}</span></div>
        </div>
      </div>
    `;
  };

  const displayRoute = (faseId: number, route: any) => {
    if (!mapInstanceRef.current) return;

    const existingRoute = routesRef.current.get(faseId);
    if (existingRoute) {
      mapInstanceRef.current.removeLayer(existingRoute);
    }

    if (route?.coordinates?.length > 0) {
      const routeCoords = route.coordinates.map((coord: any) => [coord.lat, coord.lng]);
      const polyline = L.polyline(routeCoords, {
        color: '#3B82F6',
        weight: 4,
        opacity: 0.8,
        dashArray: '10, 5'
      }).addTo(mapInstanceRef.current);

      routesRef.current.set(faseId, polyline);
    }
  };

  const applyAutoViewport = useCallback((options?: { forceFit?: boolean }) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const activeMarkers = extractActiveMarkers(osAtivasRef.current);
    const idsChanged = markerSetChanged(activeFaseIdsRef.current, activeMarkers);

    if (activeMarkers.length === 0) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: true, duration: 0.8 });
      activeFaseIdsRef.current = new Set();
      return;
    }

    if (options?.forceFit || idsChanged || activeFaseIdsRef.current.size === 0) {
      fitAllMarkers(map, activeMarkers);
    } else {
      ensureMarkersVisible(map, activeMarkers);
    }

    activeFaseIdsRef.current = new Set(activeMarkers.map((marker) => marker.faseId));
  }, []);

  const updateMarkerPosition = (faseId: number, lat: number, lng: number) => {
    const marker = markersRef.current.get(faseId);
    const map = mapInstanceRef.current;
    if (!marker || !map) return;

    marker.setLatLng([lat, lng]);

    const os = osAtivasRef.current.find((item) => item.fases.some((f) => f.id === faseId));
    const fase = os?.fases.find((f) => f.id === faseId);
    if (os && fase) {
      marker.setPopupContent(buildPopupContent(os, { ...fase, lat, lng }, lat, lng));
    }

    const activeMarkers = extractActiveMarkers(osAtivasRef.current).map((marker) =>
      marker.faseId === faseId ? { ...marker, lat, lng } : marker
    );
    ensureMarkersVisible(map, activeMarkers);
  };

  const syncMapMarkers = useCallback((map: L.Map, osList: OSAtiva[]) => {
    const activeFases = new Map<number, { os: OSAtiva; fase: FaseAtiva; lat: number; lng: number }>();

    osList.forEach((os) => {
      const faseAtiva = os.fases.find((fase) => fase.status_fase === 'Em Rota');
      if (faseAtiva && faseAtiva.lat != null && faseAtiva.lng != null) {
        activeFases.set(faseAtiva.id, {
          os,
          fase: faseAtiva,
          lat: Number(faseAtiva.lat),
          lng: Number(faseAtiva.lng)
        });
      }
    });

    markersRef.current.forEach((marker, faseId) => {
      if (!activeFases.has(faseId)) {
        map.removeLayer(marker);
        markersRef.current.delete(faseId);

        const route = routesRef.current.get(faseId);
        if (route) {
          map.removeLayer(route);
          routesRef.current.delete(faseId);
        }
      }
    });

    activeFases.forEach(({ os, fase, lat, lng }, faseId) => {
      const popupContent = buildPopupContent(os, fase, lat, lng);
      const existingMarker = markersRef.current.get(faseId);

      if (existingMarker) {
        existingMarker.setLatLng([lat, lng]);
        existingMarker.setPopupContent(popupContent);
        return;
      }

      const marker = L.marker([lat, lng], { icon: createDriverIcon() }).addTo(map);
      markersRef.current.set(faseId, marker);
      marker.bindPopup(popupContent);

      if (fase.endereco_destino) {
        calculateRoute([lat, lng], fase.endereco_destino).then((route) => {
          if (route) displayRoute(faseId, route);
        });
      }
    });
  }, []);

  const loadOSAtivas = async (options?: { silent?: boolean }) => {
    try {
      setIsFetching(true);
      if (!hasLoadedRef.current && !options?.silent) {
        setIsLoading(true);
      }

      const response = await api.get('/rastreamento/posicoes-ativas');
      const posicoesData = response.data.data?.posicoes || [];

      const osComPosicoes = posicoesData.map((posicao: any) => ({
        id: posicao.os_id,
        codigo_acesso: posicao.codigo_acesso,
        nome_falecido: posicao.nome_falecido,
        status_geral: posicao.status_fase,
        fases: [{
          id: posicao.fase_id,
          nome_fase: posicao.nome_fase,
          status_fase: posicao.status_fase,
          endereco_destino: '',
          motorista_nome: posicao.motorista_nome,
          motorista_email: '',
          lat: posicao.ultima_lat != null ? parseFloat(posicao.ultima_lat) : null,
          lng: posicao.ultima_lng != null ? parseFloat(posicao.ultima_lng) : null,
          ultima_atualizacao: posicao.ultima_atualizacao,
          status_rastreamento: posicao.status_rastreamento
        }]
      }));

      setOSAtivas(osComPosicoes);
      hasLoadedRef.current = true;
    } catch (error) {
      console.error('Erro ao carregar posições ativas:', error);

      if (!hasLoadedRef.current) {
        try {
          const fallbackResponse = await api.get('/os');
          const osData = fallbackResponse.data.os || [];
          const osComFasesAtivas = osData.filter((os: any) =>
            os.fases?.some((fase: any) => fase.status_fase === 'Em Rota')
          );
          setOSAtivas(osComFasesAtivas);
          hasLoadedRef.current = true;
        } catch (fallbackError) {
          console.error('Erro no fallback:', fallbackError);
        }
      }
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  const connectWebSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const backendUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3002';

    socketRef.current = io(backendUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socketRef.current.on('posicao_motorista_atualizada', (data) => {
      setOSAtivas((prevOS) =>
        prevOS.map((os) => {
          if (os.id !== data.osId) return os;
          return {
            ...os,
            fases: os.fases.map((fase) =>
              fase.id === data.faseId
                ? { ...fase, lat: data.lat, lng: data.lng, ultima_atualizacao: data.timestamp }
                : fase
            )
          };
        })
      );

      updateMarkerPosition(data.faseId, data.lat, data.lng);
    });

    socketRef.current.on('status_atualizado', () => {
      loadOSAtivas({ silent: true });
    });

    socketRef.current.on('connect', () => {
      socketRef.current?.emit('join_admin_room');
    });

    if (socketRef.current.connected) {
      socketRef.current.emit('join_admin_room');
    }
  };

  useEffect(() => {
    loadOSAtivas();
  }, []);

  useEffect(() => {
    const pollingInterval = setInterval(() => {
      loadOSAtivas({ silent: true });
    }, 15000);

    return () => clearInterval(pollingInterval);
  }, []);

  useEffect(() => {
    const recalibrateInterval = setInterval(() => {
      applyAutoViewport({ forceFit: true });
    }, RECALIBRATE_INTERVAL_MS);

    return () => clearInterval(recalibrateInterval);
  }, [applyAutoViewport]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    connectWebSocket();

    return () => {
      socketRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      minZoom: MIN_ZOOM,
      maxZoom: 18
    }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    if (!showHeader) {
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
    }

    return () => {
      routesRef.current.forEach((route) => {
        mapInstanceRef.current?.removeLayer(route);
      });
      routesRef.current.clear();
      markersRef.current.clear();
      activeFaseIdsRef.current = new Set();
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [showHeader]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    syncMapMarkers(map, osAtivas);
    applyAutoViewport();
  }, [osAtivas, syncMapMarkers, applyAutoViewport]);

  const showInitialLoading = isLoading && !hasLoadedRef.current;
  const showEmptyState = hasLoadedRef.current && osAtivas.length === 0;

  return (
    <div
      className={`${showHeader ? 'bg-white rounded-lg shadow-lg overflow-hidden' : 'h-full w-full bg-white'} ${className}`}
      style={!showHeader ? { margin: 0, padding: 0 } : {}}
    >
      {showHeader && (
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-blue-600" />
            Rastreamento em Tempo Real
            {isFetching && hasLoadedRef.current && (
              <span className="ml-2 inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            )}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {osAtivas.length} motorista(s) em rota
          </p>
        </div>
      )}

      <div className={`relative ${showHeader ? 'h-80' : 'h-full'}`}>
        <div ref={mapRef} className="w-full h-full" style={{ margin: 0, padding: 0, overflow: 'hidden' }} />

        {showInitialLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-[1px]">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-600">Carregando mapa...</p>
            </div>
          </div>
        )}

        {showEmptyState && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/75 pointer-events-none">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="h-6 w-6 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600">Nenhum motorista em rota no momento</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingMap;
